import { format } from 'date-fns';

/** Default UTC hour for auto-send when settings are unavailable */
export const DEFAULT_SEND_HOUR = 11;

/** Default UTC minute for auto-send when settings are unavailable */
export const DEFAULT_SEND_MINUTE = 0;

/**
 * Format the "Last Change" cell for report table items.
 * Works with both Recall and Availability report item types.
 */
export function formatLastChange(item: {
  modified_by_email: string | null;
  modified_at: string | null;
  is_deleted: boolean;
  is_manually_added: boolean;
  is_modified: boolean;
  original_quantity: number;
  quantity: number;
  counterparty_product_id: string;
  original_counterparty_product_id: string;
}): string {
  if (!item.modified_by_email || !item.modified_at) return '';
  const time = format(new Date(item.modified_at), 'HH:mm:ss dd.MM.yyyy');
  if (item.is_deleted) return `Removed by ${item.modified_by_email} at ${time}`;
  if (item.is_manually_added) return `Added by ${item.modified_by_email} at ${time}`;
  if (item.is_modified) {
    const changes: string[] = [];
    if (item.quantity !== item.original_quantity) {
      const orig = item.original_quantity.toLocaleString('en-US');
      const curr = item.quantity.toLocaleString('en-US');
      changes.push(`Qty ${orig} \u2192 ${curr}`);
    }
    if (item.counterparty_product_id !== item.original_counterparty_product_id) {
      changes.push(`PID ${item.original_counterparty_product_id} \u2192 ${item.counterparty_product_id}`);
    }
    // Defensive: server's `is_modified` should always imply at least one diff,
    // but guard against a stale flag producing " by user at time" with leading space.
    if (changes.length === 0) return '';
    return `${changes.join(', ')} by ${item.modified_by_email} at ${time}`;
  }
  return '';
}

/**
 * Compute the Riga timezone UTC offset string (e.g. "UTC+2" or "UTC+3").
 * Uses Intl.DateTimeFormat for reliable cross-browser behaviour.
 */
function getRigaOffsetStr(date: Date): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Riga',
    timeZoneName: 'shortOffset',
  }).formatToParts(date);

  const tzPart = parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  // tzPart is "GMT+2" / "GMT+3" etc — normalise to "UTC+N"
  return tzPart.replace('GMT', 'UTC');
}

/**
 * Format an ISO date string as Riga-local time with offset indicator.
 * Example: "14:30:00 13.03.2026 (Riga, UTC+2)"
 */
export function formatRigaTime(isoDate: string): string {
  const date = new Date(isoDate);
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Riga',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour12: false,
  });
  const parts = formatter.formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  const offsetStr = getRigaOffsetStr(date);

  return `${get('hour')}:${get('minute')}:${get('second')} ${get('day')}.${get('month')}.${get('year')} (Riga, ${offsetStr})`;
}

/**
 * Format auto-send time for the action bar tooltip.
 * Example: "Scheduled send at 11:00 (Riga, UTC+2)"
 */
export function formatRigaSendTime(autoSendTime: Date): string {
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Riga',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(autoSendTime);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  const offsetStr = getRigaOffsetStr(autoSendTime);
  return `Scheduled send at ${get('hour')}:${get('minute')} (Riga, ${offsetStr})`;
}
